// Derivation helpers + shared tone maps for the Executive Summary.

export const ALIGN_COLOR = (v) => {
  const p = Number(v) * 100;
  if (p >= 75) return "text-emerald-300";
  if (p >= 50) return "text-amber-300";
  return "text-rose-300";
};

export const RISK_TONE = {
  low: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  medium: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  high: "border-rose-500/40 text-rose-300 bg-rose-500/5",
};

/**
 * Smart root-cause derivation — uses the structured RootCauseAnalysis if
 * the synthesizer has produced one, otherwise falls back to the highest-
 * confidence non-business hypothesis. Always returns cautious phrasing.
 */
export function deriveRootCause(incident) {
  const rc = incident?.root_cause_analysis;
  if (rc && rc.failure_mechanism) {
    return {
      label: rc.failure_mechanism,
      detail: rc.probable_component
        ? `${rc.probable_component} · conf ${Math.round((rc.confidence || 0) * 100)}%`
        : `conf ${Math.round((rc.confidence || 0) * 100)}%`,
      agent: "Root-Cause Synthesizer",
      confidence: rc.confidence,
      lowConfidence: (rc.confidence || 0) < 0.5,
      structured: true,
    };
  }
  const hyps = (incident?.hypotheses || []).filter(
    (h) => h.domain !== "business"
  );
  if (!hyps.length) return null;
  const ranked = [...hyps].sort(
    (a, b) => (b.confidence || 0) - (a.confidence || 0)
  );
  const top = ranked[0];
  if ((top.confidence || 0) < 0.5) {
    const suspected = ranked
      .slice(0, 3)
      .map((h) => `${h.domain.replace("_", " ")} (${Math.round((h.confidence || 0) * 100)}%)`);
    return {
      label: "Root cause requires validation",
      detail: `Suspected areas: ${suspected.join(", ")}`,
      agent: ranked[0].agent_name,
      confidence: ranked[0].confidence,
      lowConfidence: true,
    };
  }
  return {
    label: top.hypothesis,
    detail: `${top.agent_name} · conf ${Math.round((top.confidence || 0) * 100)}%`,
    agent: top.agent_name,
    confidence: top.confidence,
    lowConfidence: false,
  };
}

export function deriveBusinessImpact(incident) {
  const biz = (incident?.hypotheses || []).find((h) => h.domain === "business");
  if (biz) {
    return { text: biz.hypothesis, agent: biz.agent_name };
  }
  return { text: "Business impact not yet assessed.", agent: null };
}

export function SummaryCell({ icon: Icon, label, value, tone, tid, highlight }) {
  return (
    <div
      className={`border rounded-sm p-3 hover:border-white/20 transition-colors min-h-[100px] ${
        highlight
          ? "border-amber-500/40 bg-amber-500/[0.04]"
          : "border-white/10"
      }`}
      data-testid={tid}
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-3.5 w-3.5 ${tone}`} strokeWidth={1.75} />
        <span className="cre-overline">{label}</span>
      </div>
      <div className="mt-2 text-sm leading-snug">{value}</div>
    </div>
  );
}
